import React from "react";
import { Box, Chip, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import type { FoamReading } from "../../api/foam";

/**
 * High-pressure warning and emergency stop alarms from the foam PLC (V60.1 / V60.2),
 * with the setpoints they trip at (VD74 / VD78).
 *
 * Shared by the Foam page and the Overview foam card so the two can't disagree.
 *
 * Takes the reading rather than fetching it: both callers already poll
 * /foam/latest and this must show the same row as the numbers beside it.
 */

/**
 * Five missed 60s polls. Past this the last reading is no longer evidence the
 * alarm is clear, so the chips say "no data" instead of a stale "OK".
 */
export const FOAM_ALARM_STALE_AFTER_S = 300;

type AlarmProps = {
  label: string;
  active: boolean | null | undefined;
  setpoint: number | null | undefined;
  address: string;
  /** Color when active: amber for the warning, red for the emergency stop. */
  activeColor: "warning" | "error";
  stale: boolean;
};

const Alarm: React.FC<AlarmProps> = ({
  label,
  active,
  setpoint,
  address,
  activeColor,
  stale,
}) => {
  const known = active != null && !stale;
  const chipLabel = !known ? "No data" : active ? "ACTIVE" : "OK";
  const limit =
    setpoint == null ? "—" : `${Math.round(setpoint).toLocaleString()} psi`;
  return (
    <Tooltip
      title={`${address} · trips above ${limit}${
        stale ? " · last reading is stale" : ""
      }`}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Chip
          size="small"
          label={chipLabel}
          color={!known ? "default" : active ? activeColor : "success"}
          variant={known && active ? "filled" : "outlined"}
          sx={{ fontWeight: 700, minWidth: 72 }}
        />
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            limit {limit}
          </Typography>
        </Box>
      </Stack>
    </Tooltip>
  );
};

type Props = {
  reading: Pick<
    FoamReading,
    | "hp_warning_active"
    | "hp_shutdown_active"
    | "hp_warning_setpoint"
    | "hp_shutdown_setpoint"
    | "age_seconds"
  > | null;
  /** Heading above the chips. Omit for a bare row. */
  title?: string;
  /**
   * The first reading is still on its way. Shows skeletons, because "No data"
   * would claim the PLC has nothing to say before we have even asked.
   */
  loading?: boolean;
};

export const FoamPressureAlarms: React.FC<Props> = ({
  reading,
  title = "High-pressure alarms",
  loading = false,
}) => {
  const stale =
    reading?.age_seconds != null &&
    reading.age_seconds > FOAM_ALARM_STALE_AFTER_S;
  return (
    <Box>
      {title && (
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          {title}
        </Typography>
      )}
      {loading ? (
        <Stack
          direction="row"
          spacing={3}
          rowGap={1}
          flexWrap="wrap"
          useFlexGap
        >
          {[0, 1].map((i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1}>
              <Skeleton variant="rounded" width={72} height={24} />
              <Box>
                <Skeleton variant="text" width={110} />
                <Skeleton variant="text" width={80} />
              </Box>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Stack
          direction="row"
          spacing={3}
          rowGap={1}
          flexWrap="wrap"
          useFlexGap
        >
          <Alarm
            label="Warning"
            active={reading?.hp_warning_active}
            setpoint={reading?.hp_warning_setpoint}
            address="V60.1"
            activeColor="warning"
            stale={stale}
          />
          <Alarm
            label="Emergency stop"
            active={reading?.hp_shutdown_active}
            setpoint={reading?.hp_shutdown_setpoint}
            address="V60.2"
            activeColor="error"
            stale={stale}
          />
        </Stack>
      )}
    </Box>
  );
};

export default FoamPressureAlarms;
